/**
 * A person's Activity card — `docs/activity.md` §12.2, Board 17 B and C.
 *
 * On Settings › Users & roles › <person> › Profile: who they are and what
 * their role lets them see, **<First> can also see** — a row per grant with
 * who gave it and when, its *Calendar* / *Inbox* tags and × — and **Let
 * <First> see someone** (the 380 popup: a person, what, booking, an optional
 * end date). Then **Who can see <First>**, worked out by the server from the
 * roles and the grants. A grant only ever adds; × ends it (the row is kept,
 * ended yesterday, so it reads as history — rule 2).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  monthDay, type ActivityPerson, type PeopleDirectory,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Button, Switch } from '../components/primitives.js'
import { Popover } from '../components/overlays.js'
import { DateInput } from '../components/form/index.js'
import { IconSecurity } from '../frame/icons.js'
import { Face } from '../activity/faces.js'
import { Glyph } from '../activity/glyphs.js'

type Scope = 'own' | 'branch' | 'all'

interface PersonReach {
  officeUser: boolean
  name: string
  firstName: string
  initials: string
  colour: string | null
  roleNames: string[]
  placeName: string | null
  calendarScope: Scope
  byRole: string[]
  byGrant: { name: string; what: string }[]
}

interface PersonGrant {
  id: string
  subjectId: string
  subjectName: string
  subjectColour: string | null
  calendar: boolean
  inbox: boolean
  book: boolean
  until: string | null
  grantedByName: string | null
  grantedOn: string
  live: boolean
}

/** *Activity: only their own by role*. */
export const SCOPE_WORDS: Record<Scope, string> = {
  own: 'only their own',
  branch: 'their branch',
  all: 'everyone',
}

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase()
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex h-[22px] items-center whitespace-nowrap rounded-tag bg-accent-soft px-1.5 text-[11px] font-semibold text-accent-dark">
      {children}
    </span>
  )
}

export function PersonActivityCard({ personId, canEdit, className }: {
  personId: string
  canEdit: boolean
  /** The band it sits on — the page grey under the tabs, like the Sells card. */
  className?: string
}) {
  const [reach, setReach] = useState<PersonReach | null>(null)
  const [grants, setGrants] = useState<PersonGrant[]>([])
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const addAt = useRef<HTMLButtonElement>(null)

  const load = useCallback(async () => {
    try {
      const [who, held] = await Promise.all([
        request<PersonReach>(`/people/${personId}/activity-reach`),
        request<{ items: PersonGrant[] }>(`/people/${personId}/activity-grants`),
      ])
      setReach(who)
      setGrants(held.items.filter((grant) => grant.live))
      setError(null)
    } catch (problem) {
      setReach(null)
      setError((problem as { message?: string }).message ?? null)
    }
  }, [personId])

  useEffect(() => { void load() }, [load])

  const end = async (grant: PersonGrant) => {
    try {
      await request(`/people/${personId}/activity-grants/${grant.id}/end`, { method: 'POST', body: {} })
      await load()
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That grant stays.')
    }
  }

  /* Activity is for office-app people (§5.3): a crew member's page draws no card. */
  if (!reach || !reach.officeUser) return null
  const first = reach.firstName
  const sub = [reach.roleNames.join(', '), reach.placeName,
    `Activity: ${SCOPE_WORDS[reach.calendarScope]} by role`].filter(Boolean).join(' · ')

  return (
    <div className={className}>
      <section aria-label={`${reach.name} — Activity`}
        className="rounded-record border border-line bg-surface px-[18px] py-4">
        <div className="flex items-center gap-2.5">
          <Face person={{ id: personId, name: reach.name, initials: reach.initials, colour: reach.colour }} size={36} />
          <div className="flex min-w-0 flex-col">
            <b className="truncate text-[15px]">{reach.name}</b>
            <span className="truncate text-[12px] text-text-muted">{sub}</span>
          </div>
        </div>

        <div className="mb-1.5 mt-3.5 flex items-baseline gap-2">
          <b className="text-[13.5px]">{first} can also see</b>
          <span className="text-[11.5px] text-text-muted">on top of what the role gives</span>
        </div>
        {grants.length === 0 && (
          <p className="m-0 border-b border-line-faint pb-2.5 text-[12.5px] text-text-faint">
            Nobody yet — only what the role gives.
          </p>
        )}
        {grants.map((grant) => (
          <div key={grant.id} className="flex h-[52px] items-center gap-2.5 border-b border-line-faint">
            <Face person={{ id: grant.subjectId, name: grant.subjectName, initials: initialsOf(grant.subjectName),
              colour: grant.subjectColour }} size={28} />
            <div className="flex min-w-0 flex-1 flex-col">
              <b className="truncate text-[13px]">{grant.subjectName}</b>
              <span className="truncate text-[11.5px] text-text-muted">
                {grant.grantedByName ? `granted by ${grant.grantedByName} · ` : 'granted '}
                {monthDay(grant.grantedOn)}
                {grant.until ? ` · until ${monthDay(grant.until)}` : ''}
              </span>
            </div>
            <div className="flex gap-1">
              {grant.calendar && <Tag>Calendar</Tag>}
              {grant.inbox && <Tag>Inbox</Tag>}
            </div>
            {canEdit && (
              <button type="button" aria-label={`Stop ${first} seeing ${grant.subjectName}`}
                onClick={() => void end(grant)}
                className="ml-1.5 inline-flex text-text-faint hover:text-text">
                <Glyph name="close" size={13} />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button ref={addAt} type="button" onClick={() => setAdding(true)}
            className="flex h-10 items-center gap-1.5 text-[12.5px] font-semibold text-accent">
            <Glyph name="plus" size={14} />Let {first} see someone
          </button>
        )}

        <div className="mb-1.5 mt-3.5 flex items-baseline gap-2">
          <b className="text-[13.5px]">Who can see {first}</b>
          <span className="text-[11.5px] text-text-muted">worked out — change it on their page or their role</span>
        </div>
        <div className="text-[12.5px] leading-[1.7] text-text-body">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex text-text-muted"><IconSecurity size={13} /></span>
            <span><b>By role</b> — {reach.byRole.length > 0 ? reach.byRole.join(' · ') : 'nobody else'}</span>
          </div>
          {reach.byGrant.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex text-text-muted"><Glyph name="user" size={13} /></span>
              <span><b>By grant</b> — {reach.byGrant.map((one) => `${one.name} (${one.what})`).join(' · ')}</span>
            </div>
          )}
        </div>
        {error && <p role="alert" className="m-0 mt-2 text-[12.5px] text-danger">{error}</p>}
      </section>

      {adding && (
        <GrantPopup
          anchor={addAt.current}
          personId={personId}
          first={first}
          taken={grants.map((grant) => grant.subjectId)}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); void load() }}
        />
      )}
    </div>
  )
}

/** An 18 px box — Board 17 C's *What <first> can see*. */
function Tick({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" role="checkbox" aria-checked={on} onClick={onClick}
      className="flex items-center gap-2 text-left text-[13px]">
      <span aria-hidden className={cn('inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]',
        on ? 'bg-accent text-white' : 'border-[1.5px] border-line-strong bg-surface')}>
        {on && <Glyph name="check" size={12} stroke={3} />}
      </span>
      <span>{children}</span>
    </button>
  )
}

/** *Let Tom see someone* — Board 17 C, 380 px. */
function GrantPopup({ anchor, personId, first, taken, onClose, onAdded }: {
  anchor: HTMLElement | null
  personId: string
  first: string
  /** People already granted: picking one changes that grant rather than adding a second. */
  taken: string[]
  onClose: () => void
  onAdded: () => void
}) {
  const [people, setPeople] = useState<ActivityPerson[] | null>(null)
  const [subjectId, setSubjectId] = useState<string | null>(null)
  const [calendar, setCalendar] = useState(true)
  const [inbox, setInbox] = useState(false)
  const [book, setBook] = useState(false)
  const [until, setUntil] = useState('')
  const [dated, setDated] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [picking, setPicking] = useState(false)
  const pickAt = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    let live = true
    request<PeopleDirectory>('/activity/people')
      .then((answer) => {
        if (!live) return
        /* Nobody is chosen for them: a grant opens somebody's calendar, so it is picked, never defaulted. */
        setPeople(answer.people.filter((person) => person.id !== personId))
      })
      .catch(() => { if (live) setPeople([]) })
    return () => { live = false }
  }, [personId])

  const subject = useMemo(() => people?.find((person) => person.id === subjectId) ?? null, [people, subjectId])
  const subjectFirst = subject ? subject.name.split(' ')[0] : 'them'

  const add = async () => {
    if (!subjectId) return
    setBusy(true)
    setError(null)
    try {
      await request(`/people/${personId}/activity-grants`, {
        method: 'POST',
        body: { subjectId, calendar, inbox, book: book && calendar, until: until || null },
      })
      onAdded()
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Popover open onClose={onClose} anchor={anchor} width={380} label={`Let ${first} see someone`}
      className="shadow-[0_14px_40px_rgba(20,20,10,.14)]">
      <div className="flex items-center gap-3 border-b border-line-faint px-4 py-3.5">
        <div className="flex min-w-0 flex-col">
          <b className="text-[14.5px]">Let {first} see someone</b>
          <span className="text-[12px] text-text-muted">a grant adds to the role; it never takes away</span>
        </div>
        <span className="flex-1" />
        <button type="button" aria-label="Close" onClick={onClose}
          className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full text-text-muted hover:bg-hover">
          <Glyph name="close" size={16} />
        </button>
      </div>

      <div className="flex flex-col px-4 py-3.5">
        <div className="mb-[5px] text-[11.5px] font-medium text-text-muted">Person</div>
        <button ref={pickAt} type="button" aria-label="Person" aria-haspopup="menu" aria-expanded={picking}
          onClick={() => setPicking((was) => !was)}
          className="flex h-9 items-center gap-2 rounded-button border border-line bg-surface px-2.5 text-[13px]">
          {subject
            ? <><Face person={subject} size={22} /><b>{subject.name}</b></>
            : <span className="text-text-faint">
              {people === null ? 'Loading…' : people.length ? 'Choose a person' : 'Nobody to add'}
            </span>}
          <span className="flex-1" />
          <Glyph name="chevronDown" size={14} className="text-text-muted" />
        </button>
        {picking && people && (
          <Popover open onClose={() => setPicking(false)} anchor={pickAt.current} width={346} label="People" layer={1}
            maxHeight={320} className="py-1">
            <div role="menu" className="flex flex-col overflow-y-auto">
              {people.map((person) => (
                <button key={person.id} type="button" role="menuitemradio" aria-checked={person.id === subjectId}
                  onClick={() => { setSubjectId(person.id); setPicking(false) }}
                  className={cn('mx-1 flex items-center gap-2 rounded-[9px] px-2.5 py-1.5 text-left text-[13px]',
                    person.id === subjectId ? 'bg-green-band-bg font-semibold text-accent-dark' : 'hover:bg-hover')}>
                  <Face person={person} size={22} />
                  <span className="min-w-0 flex-1 truncate">{person.name}</span>
                  {taken.includes(person.id) && <span className="text-[11px] text-text-faint">can see already</span>}
                </button>
              ))}
            </div>
          </Popover>
        )}

        <div className="mb-[5px] mt-3 text-[11.5px] font-medium text-text-muted">What {first} can see</div>
        <div className="flex flex-col gap-2">
          <Tick on={calendar} onClick={() => setCalendar((was) => !was)}><b>Calendar</b> · tasks and notes</Tick>
          <Tick on={inbox} onClick={() => setInbox((was) => !was)}><b>Inbox</b> · texts and emails</Tick>
        </div>

        <div className="mt-3 flex items-center gap-2 text-[13px]">
          <Switch label={`Can also book and reassign for ${subjectFirst}`} hideLabel checked={book && calendar}
            disabled={!calendar} onChange={setBook} />
          Can also book and reassign for {subjectFirst}
        </div>

        <div className="mb-[5px] mt-2.5 flex items-center text-[11.5px] font-medium text-text-muted">
          Until<span className="ml-auto text-text-faint">optional</span>
        </div>
        {dated || until
          ? <DateInput label="Until" value={until} onChange={setUntil} />
          : (
            <button type="button" onClick={() => setDated(true)}
              className="flex h-9 w-full items-center rounded-button border border-line bg-surface px-[11px] text-left text-[13px] text-text-faint">
              No end date
            </button>
          )}
        {error && <p role="alert" className="m-0 mt-2 text-[12.5px] text-danger">{error}</p>}
      </div>

      <div className="flex items-center gap-2.5 border-t border-line bg-surface-alt px-4 py-3">
        <span className="flex-1" />
        <Button className="h-8 text-[12.5px]" onClick={onClose}>Cancel</Button>
        <Button variant="primary" className="h-8 text-[12.5px]" disabled={busy || !subjectId || (!calendar && !inbox)}
          onClick={() => void add()}>
          <Glyph name="check" size={14} />Add
        </Button>
      </div>
    </Popover>
  )
}
